import React from "react";

import { ChevronDown, ChevronRight } from "lucide-react";

import { S } from "./strings";
import { Origin } from "./templateDiff";

type Tone = "green" | "amber" | "blue" | "gray" | "faint";

const TONES: Record<Tone, string> = {
  green: "bg-green-50 text-green-700",
  amber: "bg-amber-50 text-amber-700",
  blue: "bg-blue-50 text-blue-700",
  gray: "bg-gray-100 text-gray-600",
  faint: "bg-gray-50 text-gray-400",
};

export const Pill: React.FC<{ tone: Tone; children: React.ReactNode }> = ({
  tone,
  children,
}) => (
  <span
    className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONES[tone]}`}
  >
    {children}
  </span>
);

export const OriginPill: React.FC<{ origin: Origin }> = ({ origin }) => {
  if (origin === "added") return <Pill tone="blue">{S.addedForTenant}</Pill>;
  if (origin === "customised") return <Pill tone="blue">{S.customised}</Pill>;
  if (origin === "default") return <Pill tone="gray">{S.fromDefault}</Pill>;
  return null;
};

export const Panel: React.FC<{
  title: string;
  hint?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ title, hint, right, children, className = "" }) => (
  <section
    className={`min-w-0 rounded-lg border border-gray-200 bg-white ${className}`}
  >
    <header className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
      </div>
      {right}
    </header>
    {children}
  </section>
);

interface RowProps {
  index: number;
  title: string;
  dim: boolean;
  sub: string;
  tags: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

/** One question or section: a summary line that opens into its editor. */
export const EditableRow: React.FC<RowProps> = ({
  index,
  title,
  dim,
  sub,
  tags,
  open,
  onToggle,
  children,
}) => (
  <li className="border-t border-gray-100 first:border-t-0">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="grid w-full grid-cols-[20px_24px_minmax(0,1fr)] items-start gap-2 px-4 py-3 text-left hover:bg-gray-50 md:grid-cols-[20px_24px_minmax(0,1fr)_auto]"
    >
      <span className="pt-0.5 text-gray-400">
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </span>
      <span className="pt-px text-right font-mono text-xs tabular-nums text-gray-400">
        {index}
      </span>
      <span className="min-w-0">
        <span
          className={`block break-words text-sm font-medium ${
            dim ? "text-gray-400 line-through" : "text-gray-900"
          }`}
        >
          {title}
        </span>
        <span className="mt-0.5 block text-xs text-gray-500">{sub}</span>
      </span>
      <span className="col-start-3 flex flex-wrap items-center gap-1.5 md:col-start-auto md:justify-end">
        {tags}
      </span>
    </button>
    {open && (
      <div className="grid gap-4 px-4 pb-4 md:pl-[76px]">{children}</div>
    )}
  </li>
);

export const Field: React.FC<{
  label: string;
  help?: string;
  htmlFor: string;
  children: React.ReactNode;
}> = ({ label, help, htmlFor, children }) => (
  <div className="grid gap-1">
    <label htmlFor={htmlFor} className="text-xs font-semibold text-gray-700">
      {label}
    </label>
    {children}
    {help && <p className="text-xs text-gray-500">{help}</p>}
  </div>
);

export const inputClass =
  "w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500";

export const smallButton =
  "rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40";

export const ghostButton =
  "rounded-md px-2.5 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800";

interface RowActionsProps {
  canUp: boolean;
  canDown: boolean;
  onMove: (by: number) => void;
  onReset?: () => void;
  onRemove?: () => void;
  children?: React.ReactNode;
}

export const RowActions: React.FC<RowActionsProps> = ({
  canUp,
  canDown,
  onMove,
  onReset,
  onRemove,
  children,
}) => (
  <div className="flex flex-wrap items-center gap-2">
    {children}
    <button
      type="button"
      className={smallButton}
      disabled={!canUp}
      onClick={() => onMove(-1)}
    >
      {S.moveUp}
    </button>
    <button
      type="button"
      className={smallButton}
      disabled={!canDown}
      onClick={() => onMove(1)}
    >
      {S.moveDown}
    </button>
    {onReset && (
      <button type="button" className={ghostButton} onClick={onReset}>
        {S.reset}
      </button>
    )}
    {onRemove && (
      <button
        type="button"
        className={`${ghostButton} text-red-600 hover:text-red-700`}
        onClick={onRemove}
      >
        {S.remove}
      </button>
    )}
  </div>
);

export const Checkbox: React.FC<{
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}> = ({ id, label, checked, onChange, disabled = false }) => (
  <label
    htmlFor={id}
    className={`flex items-center gap-2 text-sm ${disabled ? "text-gray-400" : "text-gray-700"}`}
  >
    <input
      id={id}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={e => onChange(e.target.checked)}
      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
    />
    {label}
  </label>
);
